// Soluu base UUID: `0DACD14A-8063-43E5-8303-30CEF598Fxxx`. The library
// only references services and characteristics that the active spec scopes
// to this harness. Security Service `110` (and char `112` Unlock Device in
// particular) is intentionally absent — see CLAUDE.md §Hard constraints.
//
// Every handle here was verified first-hand against firmware
// `wireless_profiles/ble_soluu.c`, per W-003. The GATT specification
// document and the production Flutter app both carry confirmed errors in
// the OTA range — never take a handle from either.
const BASE_PREFIX = '0dacd14a-8063-43e5-8303-30cef598f';
function soluuUuid(suffix) {
    if (!/^[0-9a-fA-F]{3}$/.test(suffix)) {
        throw new Error(`Invalid Soluu UUID suffix: ${suffix}`);
    }
    return `${BASE_PREFIX}${suffix.toLowerCase()}`;
}
export const UUID = {
    // Configuration Service §210
    configurationService: soluuUuid('210'),
    // §211–§214 — on-device settings, all R/W uint8. Firmware registers all
    // four unconditionally (`ble_soluu.c`), so a missing one is a fault.
    pulseIntensity: soluuUuid('211'),
    volumeEnabled: soluuUuid('212'),
    volumeLevel: soluuUuid('213'),
    vibrationEnabled: soluuUuid('214'),
    // §215 LED Enabled is DELIBERATELY ABSENT — see W-008. It exists in
    // firmware but is dead: the Flutter app defines CONFIG_LIGHT_ENABLED_CHAR_UUID
    // and references it nowhere, and the adjacent §21A LED Brightness block is
    // inside a firmware comment containing non-compiling placeholder text.
    // Do not "fix" this gap by adding it.
    wifiStatus: soluuUuid('216'),
    // §217 — URL the device pings to confirm WAN reachability. Required
    // for ON_INTERNET. Firmware expects host[/path] without scheme.
    // §218 (Register Endpoint) and §219 (Event Endpoint) intentionally
    // absent — writing them risks ON_NETWORK.
    wifiPingEndpoint: soluuUuid('217'),
    // WiFi Service §310
    wifiService: soluuUuid('310'),
    wifiSsid: soluuUuid('311'),
    wifiPassword: soluuUuid('312'),
    wifiSecurity: soluuUuid('313'),
    // §314 is a command multiplexer: 0x01 (any unrecognised byte) triggers
    // connect, 0xE1 runs wifi_delete_provision(). Both harness writes to it
    // go through this one handle.
    wifiConnect: soluuUuid('314'),
    // §315 — the firmware's own reason a WiFi operation failed, carried as the
    // raw SDK status word. READ|NOTIFY over a uint32_t. Conditional: it entered
    // firmware at fw_rev 0.250514, so older units simply don't have it.
    // Undocumented in the GATT specification document — see D-009.
    wifiError: soluuUuid('315'),
    // §316 WiFi Scan Results is out of scope for spec 0002 (D-010).
    // OTA Service §410. §411 Radio FW Download and §412 App FW Download are
    // Stage 2 and deliberately not listed yet.
    otaService: soluuUuid('410'),
    // §413 is OTA Status — one byte, READ only, no CCCD, poll-only. It is
    // declared here as a constant ONLY; nothing resolves or reads it until
    // Stage 2. It earns its place now because it labels the handle the line
    // below was corrected away from: both the GATT document and the Flutter
    // app still map radio firmware version to §413 (D-001 / W-003).
    otaStatus: soluuUuid('413'),
    // §414 is Radio FW Version — an 18-byte string populated from
    // rsi_wlan_get(RSI_FW_VERSION, …), i.e. the radio module's own version.
    radioFirmwareVersion: soluuUuid('414'),
    // Standard Bluetooth services. Firmware registers all five Device
    // Information characteristics unconditionally in add_ble_services().
    deviceInformationService: 0x180a,
    modelNumberString: 0x2a24,
    // §2A25 is on Chrome's Web Bluetooth GATT blocklist and always rejects
    // with SecurityError — see W-006. Requested anyway so a future blocklist
    // relaxation works with no code change.
    serialNumberString: 0x2a25,
    firmwareRevisionString: 0x2a26,
    softwareRevisionString: 0x2a28,
    manufacturerNameString: 0x2a29,
    // Battery Service. Implemented in firmware and read by the production
    // app, but absent from the GATT specification document — see D-011.
    batteryService: 0x180f,
    batteryLevel: 0x2a19,
};
/**
 * The `optionalServices` permission grant passed to `requestDevice`. This is
 * an inert permission list, not a requirement list: a service Chrome isn't
 * told about is unreachable even when the device has it, but a service listed
 * here and absent from the device costs nothing.
 *
 * Named GRANTED rather than REQUIRED because the Battery Service is resolved
 * tolerantly — a device without it still connects.
 */
export const GRANTED_SERVICES = [
    UUID.configurationService,
    UUID.wifiService,
    UUID.otaService,
    UUID.deviceInformationService,
    UUID.batteryService,
];
//# sourceMappingURL=uuids.js.map